import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDidMount } from 'rooks';
import { FormError } from '@/core/FormError.atom';
import { useIsMounted } from '@/core/hooks/useIsMounted.hook';
import { useStateWithRef } from '@/core/hooks/useStateWithRef.hook';
import { MAX_NAME_LENGTH } from '@/main/app.constants';
import { findItemIn, getTrendItemScopedTo, getTrendStores } from '@/trend/trendDataHelper.utilities';
import { findNextColor } from '@/trendData/itemStoreColor.utilities';
import { setTrendItemColor } from '@/trendData/trend.actions';
import { PREVIEW_ID } from '@/trendData/trendData.constants';
import { getDefaultName } from '@/utilities/formula.utilities';
import { ColorPicker } from '@/workbook/ColorPicker.organism';
import { SeeqNames } from '@seeqdev/constants';
import { Icon, InputGroup } from '@seeqdev/qomponents';

interface SearchTitleProps {
  name: string;
  id: string;
  setSearchName: (name: string) => void;
  searchIconClass: string;
  defaultName: string;
  onColorChange?: (color: string) => void;
  color?: string;
  fromFormBuilder?: boolean;
  className?: any;
  types?: string[];
}

const SearchTitleUnwrapped = (props: SearchTitleProps) => {
  const { id, setSearchName, defaultName, color, onColorChange, fromFormBuilder, types } = props;
  const { t } = useTranslation();
  const [name, setName] = useStateWithRef(props.name ? props.name : '');
  const [loadingDefaultName, setLoadingDefaultName] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [originalName, setOriginalName] = useState(props.name ? props.name : '');
  const isMounted = useIsMounted();

  useEffect(() => {
    setName(props.name);
    // exhaustive deps does not work well with setters from custom hooks
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [props.name]);

  useDidMount(() => {
    if (_.isEmpty(name.current)) {
      setLoadingDefaultName(true);
      getDefaultName(t(defaultName), getTrendItemScopedTo(id), [SeeqNames.API.Flags.ExcludeGloballyScoped], types).then(
        (defaultName) => {
          if (isMounted.current) {
            setLoadingDefaultName(false);
            if (_.isEmpty(name.current)) {
              setName(defaultName);
              setSearchName(defaultName);
            }
          }
        },
      );
    }

    if (!onColorChange) {
      return;
    }

    const currentId = id || PREVIEW_ID;
    const existingItem: any = findItemIn(getTrendStores(), currentId);
    const itemColor = existingItem?.color ?? findNextColor();

    if (color !== itemColor) {
      onColorChange(itemColor);
    }
  });

  const updateName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.value;
    setName(name);
    setSearchName(name);
  };

  const setEditMode = () => {
    setIsEditing(true);
    setOriginalName(name.current);
  };

  return (
    <div>
      <h4 className="card-title searchTitleDiv">
        {isEditing ? (
          <div className="searchTitleInput">
            <InputGroup
              id="name"
              value={name.current ? name.current : ''}
              showError={(name.current?.length || 0) > MAX_NAME_LENGTH.TOOL}
              maxLength={MAX_NAME_LENGTH.TOOL}
              onChange={updateName}
              onFocus={(e) => e.target.select()}
              onBlur={() => setIsEditing(false)}
              testId="searchTitleInput"
              required={true}
              autoFocus={true}
              append={[]}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter') {
                  setIsEditing(false);
                } else if (e.key === 'Escape') {
                  setName(originalName);
                  setSearchName(originalName);
                  setIsEditing(false);
                }
              }}
            />
          </div>
        ) : (
          <div className="width100 tw:flex tw:items-center" data-testid="searchTitleDisplay">
            <div
              className="flexFill cursorPointer tw:pr-sq-5"
              onClick={setEditMode}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                  e.preventDefault();
                  setEditMode();
                }
              }}
              tabIndex={0}
              role="button">
              {name.current || t(defaultName)}
            </div>
            <div className="tw:flex tw:shrink-0 tw:items-center">
              <div
                onClick={() => setEditMode()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                    e.preventDefault();
                    setEditMode();
                  }
                }}
                tabIndex={0}
                role="button"
                className="tw:mr-2 tw:flex tw:cursor-pointer">
                <Icon icon="fa-pencil" type="color" color="#999" size="sm" />
              </div>
              {onColorChange && (
                <div className="tw:flex">
                  <ColorPicker
                    color={color}
                    itemId={id}
                    placement="bottom"
                    notifyOnSelect={(_, newColor) => {
                      onColorChange(newColor);
                      setTrendItemColor(id, newColor);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </h4>
      <hr className="searchTitleDivider" />

      {!fromFormBuilder && _.isEmpty(_.trim(name.current)) && !loadingDefaultName && (
        <FormError extraClassNames="ml25" errorText="FORM.REQUIRED_FIELD" />
      )}

      {!fromFormBuilder && name.current?.length > MAX_NAME_LENGTH.TOOL && (
        <FormError
          extraClassNames="ml25"
          errorText="NAME_LENGTH_ERROR"
          errorParameters={{ count: MAX_NAME_LENGTH.TOOL }}
        />
      )}
    </div>
  );
};

export const SearchTitle = React.memo(
  SearchTitleUnwrapped,
  (prev, next) =>
    !(
      prev.searchIconClass !== next.searchIconClass ||
      prev.className !== next.className ||
      prev.name !== next.name ||
      prev.color !== next.color
    ),
);
